const _productCache = new Map();
const _pendingRequests = new Map();

function getProductData(handle) {
  if (_productCache.has(handle)) {
    return Promise.resolve(_productCache.get(handle));
  }
  if (_pendingRequests.has(handle)) {
    return _pendingRequests.get(handle);
  }
  const promise = fetch(`${window.Shopify.routes.root}products/${handle}?view=collection-grid-json`)
    .then(r => {
      if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
      return r.json();
    })
    .then(data => {
      _productCache.set(handle, data);
      _pendingRequests.delete(handle);
      return data;
    })
    .catch(err => {
      _pendingRequests.delete(handle);
      throw err;
    });
  _pendingRequests.set(handle, promise);
  return promise;
}

const _bdSubscribers = new Set();
let _bdListenerAttached = false;
function subscribeBdUpdates(fn) {
  if (!_bdListenerAttached) {
    window.addEventListener('update-bd-text', (e) => {
      for (const cb of _bdSubscribers) cb(e);
    }, { passive: true });
    _bdListenerAttached = true;
  }
  _bdSubscribers.add(fn);
}
function unsubscribeBdUpdates(fn) {
  _bdSubscribers.delete(fn);
}

// Top level collection component
if (!customElements.get('component-collection')) {
  customElements.define('component-collection', class Collection extends HTMLElement {
    constructor() {
      super();
    }
  });
}

if (!customElements.get('component-collection-grid')) {
  customElements.define('component-collection-grid', class CollectionGrid extends HTMLElement {
    constructor() {
      super();
      this.sectionSwipers = [];
      this.props = {
        bdEnabled: this.dataset.bdEnabled === 'true',
        showPriceBadge: this.dataset.showPriceBadge,
        priceBadgeSetsTag: this.dataset.priceBadgeSetsTag,
        sectionCarouselEnabled: this.dataset.sectionCarouselEnabled === 'true'
      }
      if (this.props.bdEnabled) {
        this.props.bdDiscountType = this.dataset.bdDiscountType;
        this.props.bdAmountToSave = this.dataset.bdAmountToSave;
        this.props.bdItemsCount = this.dataset.bdItemsCount;
      }
      this.sendMarketingBlockGTMData();
      if (this.dataset.collectionSections === 'true') {
        this.updateToCollectionSections();
      }
    }

    sendMarketingBlockGTMData() {
      this.querySelectorAll('.js-marketing-block').forEach((marketingBlock, index) => {
        if (marketingBlock.dataset.marketingGtmBound === 'true') return;
        marketingBlock.dataset.marketingGtmBound = 'true';
        marketingBlock.addEventListener('click', () => {
          let link;
          if (marketingBlock.querySelector('.js-link')) {
            link = marketingBlock.querySelector('.js-link').href;
          }
          window.sendGTMData && window.sendGTMData({
            event: 'plp_marketing_tile_click',
            collection_type: window.theme?.collection?.title || '',
            tile_name: marketingBlock.dataset.title,
            tile_position: marketingBlock.dataset.position,
            tile_image: marketingBlock.querySelector('.js-marketing-image').src,
            tile_link: link
          });
        }, { passive: true });
        const imgId = marketingBlock.dataset.imgid;
        const desktopAnchors = marketingBlock.dataset.mad.split(',');
        const mobileAnchors = marketingBlock.dataset.mab.split(',');
        const parsedPos = parseInt(marketingBlock.dataset.position, 10);
        const anchorIndex = Number.isFinite(parsedPos) ? parsedPos - 1 : index;
        let anchorId = 'marketing-block-';
        if (mobileAnchors[anchorIndex] === imgId || desktopAnchors[anchorIndex] === imgId) {
          anchorId += anchorIndex;
        }
        if (!marketingBlock.dataset.marketingClone) {
          marketingBlock.setAttribute('id', anchorId);
        }
      });
    }

    updateToCollectionSections() {
      this.props.sectionCarouselEnabled = this.dataset.sectionCarouselEnabled === 'true';

      if (this.dataset.displayCollectionSections !== 'true') {
        this.displayOriginalCollectionGrid();
        return;
      }

      if (!this.querySelector('.js-collection-grid-sections')) {
        this.displayOriginalCollectionGrid();
        return;
      }
      const sourceGrid = this.querySelector('.js-collection-grid-original');
      if (!sourceGrid) {
        this.displayOriginalCollectionGrid();
        return;
      }
      const marketingHasSection = (el) => {
        const s = el.dataset && el.dataset.section;
        return s !== undefined && s !== null && String(s).trim() !== '';
      };
      const sectionsContainer = this.querySelector('.js-collection-grid-sections');
      const collectionSections = this.querySelectorAll('.js-collection-grid-sections-section:not(.js-collection-grid-sections-section-generic)');
      const maxProductsPerSection = this.props.sectionCarouselEnabled ? Number.POSITIVE_INFINITY : 3;
      const originalProducts = Array.from(sourceGrid.querySelectorAll('.js-product-item'));
      const normalizedCollectionSectionNames = new Set();

      collectionSections.forEach((collSection) => {
        const secGrid = collSection.querySelector('.js-collection-grid');
        if (!secGrid) return;

        // Ensure section keeps grid layout when carousel is disabled.
        secGrid.classList.remove('collection-grid--with-section-carousel');
        const existingCarouselContainer = collSection.querySelector('.js-collection-section-carousel-container');
        if (existingCarouselContainer) {
          existingCarouselContainer.remove();
        }
        const existingCarousel = collSection.querySelector('.js-collection-section-carousel');
        if (existingCarousel) {
          existingCarousel.remove();
        }

        secGrid.replaceChildren();

        const collName = this.normalizeSectionName(collSection.querySelector('.js-section-title').dataset.sectionTitle);
        normalizedCollectionSectionNames.add(collName);
        const mktBlocks = sourceGrid.querySelectorAll(`.js-marketing-block[data-section='${collName}' i]`);
        if (mktBlocks[0]) {
          const mktClone = mktBlocks[0].cloneNode(true);
          mktClone.removeAttribute('id');
          mktClone.dataset.marketingClone = 'true';
          secGrid.appendChild(mktClone);
        }
        let renderedCount = 0;
        originalProducts.forEach((product) => {
          if (renderedCount >= maxProductsPerSection) return;
          const productSections = this.getNormalizedProductSections(product);
          if (productSections.has(collName)) {
            secGrid.appendChild(product.cloneNode(true));
            renderedCount++;
          }
        });
      });

      const genericSection = this.querySelector('.js-collection-grid-sections-section-generic');
      const genericSectionGrid = genericSection && genericSection.querySelector('.js-collection-grid');
      if (genericSectionGrid) {
        genericSectionGrid.replaceChildren();
        const markMarketingClone = (node) => {
          if (node.classList && node.classList.contains('js-marketing-block')) {
            node.removeAttribute('id');
            node.dataset.marketingClone = 'true';
          }
        };
        Array.from(sourceGrid.children).forEach((child) => {
          if (child.classList && child.classList.contains('js-product-item')) {
            const productSections = this.getNormalizedProductSections(child);
            const belongsToKnownSection = Array.from(productSections).some((sec) => normalizedCollectionSectionNames.has(sec));
            if (belongsToKnownSection) return;
            genericSectionGrid.appendChild(child.cloneNode(true));
            return;
          }

          if (child.classList && child.classList.contains('js-marketing-block')) {
            if (marketingHasSection(child)) return;
            const copy = child.cloneNode(true);
            markMarketingClone(copy);
            genericSectionGrid.appendChild(copy);
            return;
          }
          const copy = child.cloneNode(true);
          copy.querySelectorAll('.js-marketing-block').forEach((node) => {
            if (marketingHasSection(node)) {
              node.remove();
            } else {
              markMarketingClone(node);
            }
          });
          genericSectionGrid.appendChild(copy);
        });
      }

      if (this.props.sectionCarouselEnabled) {
        this.initializeSectionProductCarousels();
      }

      this.sendMarketingBlockGTMData();

      this.querySelectorAll('.collection-grid-sections-section').forEach((section) => {
        if (section.querySelectorAll('.js-product-item').length) {
          section.style.display = 'block';
        } else {
          section.style.display = 'none';
        }
      });

      if (sectionsContainer) {
        sectionsContainer.style.display = 'block';
      }

      this.displayOriginalCollectionGrid();
    }

    normalizeSectionName(sectionName) {
      return (sectionName || '').toString().trim().toLowerCase();
    }

    getNormalizedProductSections(productNode) {
      const mappedSections = new Set();

      if (productNode && productNode.dataset) {
        const sectionsRaw = productNode.dataset.sections || '';
        sectionsRaw.split(',').forEach((sectionName) => {
          const normalizedSection = this.normalizeSectionName(sectionName);
          if (normalizedSection) mappedSections.add(normalizedSection);
        });

        const singleSection = this.normalizeSectionName(productNode.dataset.collectionSection);
        if (singleSection) mappedSections.add(singleSection);
      }

      return mappedSections;
    }

    initializeSectionProductCarousels() {
      this.sectionSwipers.forEach((instance) => {
        if (instance && typeof instance.destroy === 'function') {
          instance.destroy(true, true);
        }
      });
      this.sectionSwipers = [];

      const sectionNodes = this.querySelectorAll('.js-collection-grid-sections-section:not(.js-collection-grid-sections-section-generic)');
      sectionNodes.forEach((sectionNode) => {
        const sectionGrid = sectionNode.querySelector('.js-collection-grid');
        if (!sectionGrid) return;
        sectionGrid.classList.remove('collection-grid--with-section-carousel');

        const sectionProducts = Array.from(sectionGrid.querySelectorAll('.js-product-item'));
        if (sectionProducts.length <= 1) return;
        if (typeof window.Swiper !== 'function') return;

        const existingCarouselContainer = sectionNode.querySelector('.js-collection-section-carousel-container');
        if (existingCarouselContainer) {
          existingCarouselContainer.remove();
        }

        const existingCarousel = sectionNode.querySelector('.js-collection-section-carousel');
        if (existingCarousel) {
          existingCarousel.remove();
        }

        const carouselContainer = document.createElement('div');
        carouselContainer.className = 'js-collection-section-carousel-container collection-section-carousel-container';

        const carousel = document.createElement('div');
        carousel.className = 'js-collection-section-carousel collection-section-carousel swiper';

        const wrapper = document.createElement('div');
        wrapper.className = 'swiper-wrapper';

        sectionProducts.forEach((productNode) => {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide collection-section-carousel-slide';
          slide.appendChild(productNode);
          wrapper.appendChild(slide);
        });

        const pagination = document.createElement('div');
        pagination.className = 'js-collection-section-carousel-pagination collection-section-carousel-pagination';

        const controls = document.createElement('div');
        controls.className = 'collection-section-carousel-controls';

        const prevBtn = document.createElement('button');
        prevBtn.type = 'button';
        prevBtn.className = 'js-collection-section-carousel-prev collection-section-carousel-button collection-section-carousel-button-prev';
        prevBtn.setAttribute('aria-label', 'Previous products');
        prevBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="21" height="18" viewBox="0 0 21 18" fill="none" aria-hidden="true"><path d="M8.57642 16.4459L0.707032 8.57654L8.57642 0.707155" stroke="black" stroke-linecap="square"/><path d="M1.90065 8.57678H19.707" stroke="black" stroke-linecap="square"/></svg>';

        const nextBtn = document.createElement('button');
        nextBtn.type = 'button';
        nextBtn.className = 'js-collection-section-carousel-next collection-section-carousel-button collection-section-carousel-button-next';
        nextBtn.setAttribute('aria-label', 'Next products');
        nextBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="21" height="18" viewBox="0 0 21 18" fill="none" aria-hidden="true"><path d="M11.6309 16.4459L19.5002 8.57648L11.6309 0.707094" stroke="black" stroke-linecap="square"/><path d="M18.3064 8.57666H0.5" stroke="black" stroke-linecap="square"/></svg>';

        controls.appendChild(prevBtn);
        controls.appendChild(pagination);
        controls.appendChild(nextBtn);

        carousel.appendChild(wrapper);
        carouselContainer.appendChild(controls);
        carouselContainer.appendChild(carousel);
        sectionGrid.classList.add('collection-grid--with-section-carousel');
        sectionGrid.appendChild(carouselContainer);

        this.bindSwiperCardHover(carousel);

        const swiperInstance = new window.Swiper(carousel, {
          slidesPerView: 2,
          spaceBetween: 12,
          watchOverflow: true,
          navigation: {
            prevEl: prevBtn,
            nextEl: nextBtn
          },
          pagination: {
            el: pagination,
            clickable: true
          },
          breakpoints: {
            768: {
              slidesPerView: 2,
              spaceBetween: 16
            },
            1240: {
              slidesPerView: 2,
              spaceBetween: 24
            }
          }
        });

        this.sectionSwipers.push(swiperInstance);
      });
    }

    bindSwiperCardHover(carousel) {
      const productCards = carousel.querySelectorAll('component-collection-product');
      productCards.forEach((productCard) => {
        const productImg = productCard.querySelector('.js-product-img');
        const hoverImg = productCard.querySelector('.js-img-hover');
        if (!productImg || !hoverImg || !hoverImg.dataset.src) return;

        productImg.addEventListener('mouseenter', () => {
          if (typeof productCard.loadHoverImg === 'function') {
            productCard.loadHoverImg();
          }
        }, { once: true, passive: true });
      });
    }

    displayOriginalCollectionGrid() {
      const originalCollectionGrid = this.querySelector('.js-collection-grid-original');
      if (!originalCollectionGrid) return;
      const marketingHasSection = (el) => {
        const s = el.dataset && el.dataset.section;
        return s !== undefined && s !== null && String(s).trim() !== '';
      };
      Array.from(originalCollectionGrid.querySelectorAll('.js-marketing-block')).forEach((node) => {
        if (marketingHasSection(node)) node.remove();
      });
      const hasProducts = originalCollectionGrid.querySelectorAll('component-collection-product').length > 0;
      if (!hasProducts) {
        originalCollectionGrid.classList.add('hidden');
        return;
      }
      if (this.dataset.displayCollectionSections === 'true' && this.dataset.collectionSections === 'true' && this.querySelector('.js-collection-grid-sections')) {
        originalCollectionGrid.classList.add('hidden');
      } else {
        originalCollectionGrid.classList.remove('hidden');
      }
    }
  });
}


if (!customElements.get('component-collection-grid-video')) {
  customElements.define('component-collection-grid-video', class CollectionGridVideo extends HTMLElement {
    constructor() {
      super();
      this.props = {
        videoTitle: this.dataset.videoTitle,
        videoPosition: this.dataset.videoPosition,
        videoPositionMobile: this.dataset.videoPositionMobile,
        videoSrc: this.dataset.videoSrc,
        videoMobileSrc: this.dataset.videoMobileSrc
      }
      this.handleVideoImageClick();
      this.handleVideoEnded();
    }
    handleVideoImageClick() {
      if (this.querySelector('.js-video-image')) {
        this.querySelector('.js-video-image').addEventListener('click', () => {
          if (window.innerWidth <= 768 && this.props.videoMobileSrc) {
            this.querySelector('.js-video-src').src = this.props.videoMobileSrc;
          } else {
            this.querySelector('.js-video-src').src = this.props.videoSrc;
          }
          this.querySelector('.js-video').load();
          this.querySelector('.js-video').classList.remove('hidden');
          this.querySelector('.js-video').play();
          this.querySelector('.js-video-image').classList.add('hidden');
          this.sendVideoPlayGTMData();
        });
      }
    }
    handleVideoEnded() {
      this.querySelector('.js-video').addEventListener('ended', () => {
        this.querySelector('.js-video').classList.add('hidden');
        this.querySelector('.js-video-image').classList.remove('hidden');
      });
    }
    sendVideoPlayGTMData() {
      window.sendGTMData && window.sendGTMData({
        event: 'home_page_module_click',
        home_module_name: `Collection Video - ${window.theme?.collection?.title || ''}`
      });
    }
  });
}

if (!customElements.get('collection-grid-skeleton-loader')) {
  customElements.define('collection-grid-skeleton-loader', class CollectionGridSkeletonLoader extends HTMLElement {
    constructor() {
      super();
      document.addEventListener('show-collection-skeleton-loader', () => {
        this.querySelector('.js-grid-skeleton').classList.remove('hidden');
      });
      document.addEventListener('hide-collection-skeleton-loader', () => {
        this.querySelector('.js-grid-skeleton').classList.add('hidden');
      });
    }
  });
}

if (!customElements.get('component-collection-product')) {
  customElements.define('component-collection-product', class CollectionProduct extends HTMLElement {
    constructor() {
      super();
      this.dom = {
        collectionComponent: document.querySelector('component-collection'),
        parentComponent: document.querySelector('component-collection-grid'),
        template: this.querySelector('.js-template')
      }
      this.state = {
        product: '',
        availableSize: '',
        siblingProduct: ''
      }
      if (this.dom.parentComponent.props.bdEnabled) {
        this.state.bdAmountToSave = parseInt(this.dom.parentComponent.props.bdAmountToSave, 10);
        this.state.bdItemsCount = parseInt(this.dom.parentComponent.props.bdItemsCount, 10);
      }
      this.props = {
        id: this.dataset.id,
        img: this.dataset.img,
        hoverImg: this.dataset.hoverImg,
        title: this.dataset.title,
        position: this.dataset.position,
        showColorSwatches: this.dataset.showColorSwatches === 'true',
        color: this.dataset.color,
        productHandle: this.dataset.productHandle,
        braSize: this.dataset.braSize,
        alphaSize: this.dataset.alphaSize,
        sizeType: this.dataset.sizeType,
        isFinalSale: this.dataset.isFinalSale === 'true',
        hasDiscountTag: this.dataset.hasDiscountTag === 'true',
        tags: this.dataset.tags.replaceAll(' ', '').split(','),
        bdFirstItemDiscountEnabled: this.dataset.bdFirstItemDiscountEnabled === 'true',
        bdPlpLabelTemplate: this.dataset.bdPlpLabelTemplate,
        bdProductType: this.dataset.bdProductType,
        bdTag: this.dataset.bdTag,
        collectionSection: this.dataset.collectionSection,
        defaultImgSize: '335'
      }
      if (this.dom.parentComponent.props.bdEnabled) {
        this.props.bdDiscountType = this.dom.parentComponent.props.bdDiscountType;
      }
    }
    async connectedCallback() {
      if (!this.props.showColorSwatches) {
        this.querySelector('.js-colors').classList.remove('colors');
      }
      this.intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.render();
            this.intersectionObserver.unobserve(this);
          }
        });
      }, { rootMargin: '200px' });
      this.intersectionObserver.observe(this);
      if (this.dom.parentComponent.props.bdEnabled) {
        this.renderBundleDiscountPromoBadge(this, this.props.isFinalSale, this.props.hasDiscountTag);
        this.handleBraPromoChange();
      }
    }
    disconnectedCallback() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      if (this._bdHandler) {
        unsubscribeBdUpdates(this._bdHandler);
        this._bdHandler = null;
      }
    }
    handleBraPromoChange() {
      this._bdHandler = (data) => {
        if (data.detail && data.detail.amountToSave) {
          this.state.bdAmountToSave = data.detail.amountToSave;
          this.state.bdItemsCount = parseInt(data.detail.bdItemsCount);
        }
        this.renderBundleDiscountPromoBadge(this, this.props.isFinalSale, this.props.hasDiscountTag);
      };
      subscribeBdUpdates(this._bdHandler);
    }
    getProduct() {
      return getProductData(this.props.productHandle);
    }
    render() {
      const template = this.dom.template;
      this.getProduct().then((product) => {
        this.state.product = product;
        if (this.state.product && this.state.product.title != null) {
          requestAnimationFrame(() => {
            if (this.state.product.colors) {
              this.state.siblingProduct = this.state.product.colors[this.props.color];
            }
            if (this.props.showColorSwatches) {
              this.renderSwatches(template);
            }
            this.renderPriceBadge(template);
            this.afterRender();
          });
        }
      });
    }
    renderImages(img, hoverImg) {
      if (img) {
        const imgEl = this.querySelector('.js-img');
        imgEl.src = img;
        imgEl.srcset = getSrcset(img, this.props.defaultImgSize);
      }
      if (hoverImg) {
        const hoverImgEl = this.querySelector('.js-img-hover');
        hoverImgEl.dataset.src = hoverImg;
        hoverImgEl.src = '';
        hoverImgEl.srcset = '';
        this.querySelector('.js-product-img').classList.add('product-img');
        hoverImgEl.classList.add('hover-img--lazyload');
      } else {
        this.querySelector('.js-product-img').classList.remove('product-img');
      }
    }
    renderSwatches(template) {
      if (this.state.product?.colors && Object.keys(this.state.product.colors).length > 0) {
        let colorCount = 0;
        for (const color in this.state.product.colors) {
          let showColorSwatch = false;
          let hasAvailableSize;
          let availableSize;
          if (this.props.sizeType === 'formFit' && this.props.formFitSize) {
            hasAvailableSize = this.state.product.colors[color].availableSizes.some(el => el.includes(this.props.formFitSize));
            availableSize = this.props.formFitSize;
          } else if (this.props.sizeType === 'alpha' && this.props.alphaSize) {
            hasAvailableSize = this.state.product.colors[color].availableSizes.some(el => el.includes(this.props.alphaSize));
            availableSize = this.props.alphaSize;
          } else if (this.props.sizeType === 'bra' && this.props.braSize) {
            const range = this.getRange(this.props.braSize);
            hasAvailableSize = this.state.product.colors[color].availableSizes.some(el => el.includes(this.props.braSize) || el.includes(range));
            availableSize = this.props.braSize;
          } else {
            showColorSwatch = true;
          }
          if (hasAvailableSize) {
            this.state.availableSize = availableSize;
            showColorSwatch = true;
          }
          if (showColorSwatch) {
            colorCount++;
            // Prepare color swatch to be added
            const colorTemplate = template.querySelector('.js-template-color').content.cloneNode(true);
            if (window.theme.colors[color]) {
              // Set CSS custom properties for swatch styling
              if (window.theme.colors[color].image) {
                colorTemplate.querySelector('.js-label').style.setProperty('--swatch-image', `url(${window.theme.colors[color].image}`);
              }
              colorTemplate.querySelector('.js-label').style.setProperty('--swatch-color', window.theme.colors[color].color);
              colorTemplate.querySelector('.js-label').style.setProperty('--swatch-border-color', '#fff');
            }
            if (color === this.props.color) {
              colorTemplate.querySelector('.js-input').checked = true;
              colorTemplate.querySelector('.js-color').classList.add("first");
            }
            colorTemplate.querySelector('.js-label').setAttribute('for', `color_${color}_${this.props.id}`);
            colorTemplate.querySelector('.js-label').dataset.color = color;
            colorTemplate.querySelector('.js-input').value = color;
            colorTemplate.querySelector('.js-input').id = `color_${color}_${this.props.id}`;
            template.querySelector('.js-colors').appendChild(colorTemplate);
          }
          template.querySelector('.js-colors').classList.add('opacity-1');
        }
        // Display count of additional hidden colors on mobile
        if (colorCount > 5) {
          const colorCountTemplate = template.querySelector('.js-template-color-count').content.cloneNode(true);
          colorCountTemplate.querySelector('.js-color-count-mobile').innerHTML = `+${colorCount - 5}`;
          if (colorCount > 10) {
            colorCountTemplate.querySelector('.js-color-count').innerHTML = `+${colorCount - 10}`;
          } else {
            colorCountTemplate.querySelector('.js-color-count').remove();
          }
          template.querySelector('.js-colors').appendChild(colorCountTemplate);
        }
      } else {
        // Remove color class in color swatches wrapper if no colors available (for example, for a bundle product)
        template.querySelector('.js-colors').classList.remove('colors');
      }
    }
    renderPriceBadge(template) {
      if (this.dom.parentComponent.props.showPriceBadge != 'none' && template.querySelector('.js-template-price-badge')) {
        const product = this.state.siblingProduct ? this.state.siblingProduct : this.state.product;
        const price = product.price;
        const comparePrice = product.compare_at_price;
        if (template.querySelector('.js-price-badge')) {
          template.querySelector('.js-price-badge').remove();
        }
        const priceBadgeTemplate = template.querySelector('.js-template-price-badge').content.cloneNode(true);
        if (comparePrice > price) {
          priceBadgeTemplate.querySelector('.js-price-badge-percent').innerHTML = `${Math.floor((1 - (price / comparePrice)) * 100)}%`;
          if (this.dom.parentComponent.props.showPriceBadge === 'all' || (this.dom.parentComponent.props.showPriceBadge === 'sets_only' && product.tags.includes(this.dom.parentComponent.props.priceBadgeSetsTag))) {
            template.querySelector('.js-price-badge-container').appendChild(priceBadgeTemplate);
          }
        }
      }
    }
    renderBundleDiscountPromoBadge(template, isFinalSale, hasDiscountTag) {
      const bdTextEl = template.querySelector('.js-bd-text');
      if (!bdTextEl) return;
      if (!this.props.bdPlpLabelTemplate || !this.state.bdAmountToSave || this.state.bdAmountToSave === 'discount_reached' || isFinalSale || !hasDiscountTag) {
        bdTextEl.classList.add('hidden');
      } else {
        bdTextEl.classList.remove('hidden');
        let count = this.state.bdItemsCount + 1;
        if (count <= 1 && this.props.bdFirstItemDiscountEnabled) {
          count = 1;
        } else if (count < 2 && !this.props.bdFirstItemDiscountEnabled) {
          count = 2;
        }
        const tier_ordinal = count > 4 ? 'fifth' : count === 4 ? 'fourth' : count === 3 ? 'third' : count === 2 ? 'second' : 'first';
        bdTextEl.innerHTML = this.props.bdPlpLabelTemplate.replace('#product_type', this.props.bdProductType).replace('#tier_number', count).replace('#tier_ordinal', tier_ordinal).replace('#discount', this.props.bdDiscountType === 'percentage' ? `${this.state.bdAmountToSave}%` : toMoney(this.state.bdAmountToSave));
      }
    }
    updatePrice() {
      let price = this.state.product.price;
      let comparePrice = this.state.product.compare_at_price;
      if (this.state.siblingProduct) {
        price = this.state.siblingProduct.price;
        comparePrice = this.state.siblingProduct.compare_at_price;
      }
      this.querySelector('.js-price').textContent = toMoney(price, { cents: false, no_zeros: true });
      if (Number(comparePrice) > Number(price)) {
        this.querySelector('.js-compare-price').textContent = toMoney(comparePrice, { cents: false, no_zeros: true });
        this.querySelector('.js-compare-price').classList.add('compare-price');
      } else {
        this.querySelector('.js-compare-price').textContent = '';
        this.querySelector('.js-compare-price').classList.remove('compare-price');
      }
    }
    async afterRender() {
      this.handleColorSwatchChange();
      this.handleImageHover();
      this.handleItemClickGTMData();
    }
    handleImageHover() {
      const productImg = this.querySelector('.js-product-img');
      if (productImg.matches(':hover')) {
        this.loadHoverImg();
        return;
      }
      productImg.addEventListener('mouseenter', () => this.loadHoverImg(), { once: true, passive: true });
    }
    loadHoverImg() {
      const hoverImg = this.querySelector('.js-img-hover');
      if (hoverImg && hoverImg.dataset.src) {
        hoverImg.srcset = getSrcset(hoverImg.dataset.src, '335');
        hoverImg.src = hoverImg.dataset.src;
        hoverImg.classList.remove('hover-img--lazyload');
      }
    }
    handleColorSwatchChange() {
      this.querySelectorAll('.js-colors .js-input').forEach(color => {
        color.addEventListener('change', () => {
          this.state.siblingProduct = this.state.product.colors[color.value];
          this.querySelectorAll('.js-link').forEach(link => {
            // If filtered by bra size, include size in link to populate PDP
            if (this.state.availableSize) {
              link.href = `${this.state.siblingProduct.url}${this.state.siblingProduct.url.includes('?') ? '&' : '?'}size=${this.state.availableSize}`;
            } else {
              link.href = this.state.siblingProduct.url;
            }
          });
          let img;
          let hoverImg;
          img = this.state.siblingProduct.images[0].src;
          if (this.state.siblingProduct.images[1]) {
            hoverImg = this.state.siblingProduct.images[1].src;
          }
          this.renderImages(img, hoverImg);
          this.updatePrice();
          this.renderPriceBadge(this);
          this.sendColorChangeGTMData(color.value);
          if (this.dom.parentComponent.props.bdEnabled) {
            this.renderBundleDiscountPromoBadge(this, isFinalSale(this.state.siblingProduct.tags), this.state.siblingProduct.tags.includes(this.props.bdTag));
          }
        });
      });
      this.querySelectorAll('.js-colors .js-label').forEach(color => {
        color.addEventListener('mouseover', () => {
          this.preloadImg(color.dataset.color, 0);
          this.preloadImg(color.dataset.color, 1);
        }, { once: true, passive: true });
      });
    }
    getRange(size) {
      let range;
      let bandSize = size.substring(0, 2);
      let cupSize = size.substring(2);
      if (size.includes('½')) {
        range = `${bandSize}${cupSize.replace('½', '')}-${cupSize}`;
      } else {
        range = `${bandSize}${cupSize}-${cupSize}½`;
      }
      return range;
    }
    preloadImg(color, imgIndex) {
      const newImage = this.state.product.colors[color].images[imgIndex];
      if (newImage) {
        const preloadImg = this.querySelector('.js-img').cloneNode();
        preloadImg.src = newImage.src;
        preloadImg.srcset = getSrcset(newImage.src, this.props.defaultImgSize);
      }
    }
    handleItemClickGTMData() {
      this.querySelectorAll('.js-link').forEach(el => {
        el.addEventListener('click', () => {
          window.sendGTMData && window.sendGTMData({
            event: 'plp_item_click',
            collection_name: window.theme?.collection?.title || '',
            collection_type: window.theme?.collection?.title || '',
            collection_item: this.props.title,
            collection_item_position: this.props.position,
            collection_item_image: this.state.siblingProduct.images[0].src,
            collection_item_text: this.props.title,
            link_url: this.state.siblingProduct.url
          });
        });
      });
    }
    sendColorChangeGTMData(color) {
      window.sendGTMData && window.sendGTMData({
        event: 'plp_item_color_click',
        collection_name: window.theme?.collection?.title || '',
        collection_type: window.theme?.collection?.title || '',
        collection_item: this.props.title,
        collection_item_position: this.props.position,
        collection_item_color_image: this.state.siblingProduct.images[0].src,
        collection_item_color: color
      });
    }
  });
}

if (!customElements.get('component-collection-grid-paginate')) {
  customElements.define('component-collection-grid-paginate', class CollectionGrid extends HTMLElement {
    constructor() {
      super();
      }

      connectedCallback() {
        this.updatePaginationLinks();
      }

      updatePaginationLinks() {
        document.querySelectorAll('component-collection-grid-paginate a').forEach((link) => {
          if (link.href !=='') {
            const searchUrl = window.location.search.replaceAll(/&page=\d*/g, '');
            const params = new URLSearchParams(link.href);
            if (params.get('page')) {
              link.href = `${window.location.pathname}${searchUrl}&page=${params.get('page')}`;
            }
          }
        });

      }
  });
}
